import { ifcModel } from '../../classes/ifcModel';
import { logger } from '../../../../shared/utils/logger';
import { useGlobalStore } from '../../stores';

export interface IFCGeometryData {
  modelId: string;
  ifcModelInstance: ifcModel;
  storeys: any[];
  categories: string[];
  isLoaded: boolean;
  center: [number, number] | null;
  extent: [number, number, number, number] | null;
}

class IFCService {
  private static instance: IFCService;
  private geometryCache: Map<string, IFCGeometryData> = new Map();
  private currentModelId: string | null = null;

  private constructor() {}

  public static getInstance(): IFCService {
    if (!IFCService.instance) {
      IFCService.instance = new IFCService();
    }
    return IFCService.instance;
  }

  /**
   * Carga un modelo IFC y almacena su geometría
   */
  public async loadModel(file: File, map: any, mapService?: any): Promise<IFCGeometryData> {
    try {
      logger.log('IFC_SERVICE', '🚀 IFCService: Iniciando carga de modelo IFC...');
      
      // Crear nueva instancia de ifcModel
      const ifcModelInstance = new ifcModel();
      
      // Inicializar el modelo con el mapa y crear la capa BIM ANTES de cargar el IFC
      if (map) {
        ifcModelInstance.map = map;
        
        // Crear o obtener la capa BIM
        let bimLayer = map.getLayers().getArray().find((layer: any) => layer.get('name') === 'bim-layer');
        if (!bimLayer) {
          const VectorSource = (await import('ol/source/Vector')).default;
          const VectorLayer = (await import('ol/layer/Vector')).default;
          const Style = (await import('ol/style/Style')).default;

          const Stroke = (await import('ol/style/Stroke')).default;
          
          bimLayer = new VectorLayer({
            source: new VectorSource(),
            style: new Style({
              stroke: new Stroke({ color: '#000000', width: 2 })
            })
          });
          bimLayer.set('name', 'bim-layer');
          bimLayer.setVisible(true);
          map.addLayer(bimLayer);
        }
        
        // Asignar bimLayer ANTES de cargar el modelo IFC
        ifcModelInstance.bimLayer = bimLayer;
        logger.log('IFC_SERVICE', '✅ IFCService: bimLayer asignado antes de cargar IFC');
      }

      // Cargar el modelo IFC (ahora bimLayer ya está disponible)
      await ifcModelInstance.loadIfcModel(file);
      logger.log('IFC_SERVICE', '✅ IFCService: Modelo IFC cargado');
      
      // Actualizar el ifcModel en el GUIController del MapService
      if (mapService && mapService.updateIfcModel) {
        logger.log('IFC_SERVICE', '🔗 IFCService: Actualizando ifcModel en MapService...');
        mapService.updateIfcModel(ifcModelInstance);
      } else {
        logger.warn('IFC_SERVICE', '⚠️ IFCService: No se pudo actualizar ifcModel en MapService');
      }
      
      // Obtener datos del modelo
      const storeys = await ifcModelInstance.getStoreys();
      const categories = ifcModelInstance.getAvailableCategories();
      
      logger.log('IFC_SERVICE', '📊 IFCService: Datos obtenidos del modelo:');
      logger.log('IFC_SERVICE', '   - Storeys:', storeys);
      logger.log('IFC_SERVICE', '   - Categories:', categories);
      
      // Verificar si geoElementsTreeXZ se construyó correctamente
      const geoElementsTreeXZ = ifcModelInstance.getGeoElementsTreeXZ();
      logger.log('IFC_SERVICE', '🔍 IFCService: geoElementsTreeXZ después de cargar:', geoElementsTreeXZ);
      logger.log('IFC_SERVICE', '🔍 IFCService: Claves en geoElementsTreeXZ:', Object.keys(geoElementsTreeXZ));
      
      // Verificar elementsTree también (usando método público)
      logger.log('IFC_SERVICE', '🔍 IFCService: Verificando elementsTree...');
      
      // Sincronizar elevación calculada con el store global
      const elevation = ifcModelInstance.getElevation();
      const elevationType = ifcModelInstance.getElevationType();
      
      if (elevation > 0) {
        logger.log('IFC_SERVICE', `📏 IFCService: Sincronizando elevación calculada: ${elevation}m (${elevationType})`);
        
        // Actualizar el store global con la elevación calculada
        const { setElevation, setElevationType } = useGlobalStore.getState();
        setElevation(elevation);
        setElevationType(elevationType);
        
        logger.log('IFC_SERVICE', '✅ IFCService: Elevación sincronizada con store global');
      } else {
        logger.log('IFC_SERVICE', '⚠️ IFCService: No se encontró elevación calculada en el modelo');
      }
      
      // Generar ID único para el modelo
      const modelId = Date.now().toString();
      
      // Crear objeto de datos de geometría
      const geometryData: IFCGeometryData = {
        modelId,
        ifcModelInstance,
        storeys,
        categories,
        isLoaded: true,
        center: [0, 0],
        extent: null
      };
      
      // Almacenar en caché
      this.geometryCache.set(modelId, geometryData);
      this.currentModelId = modelId;
      
      logger.log('IFC_SERVICE', '✅ IFCService: Modelo cargado y almacenado en caché');
      return geometryData;
      
    } catch (error) {
      console.error('❌ IFCService: Error al cargar modelo:', error);
      throw error;
    }
  }

  /**
   * Establece la elevación (altura) del IFC y su tipo de referencia vertical.
   */
  public async setModelElevation(elevationMeters: number, type: 'ortometric' | 'ellipsoidal' = 'ortometric'): Promise<boolean> {
    const ifcModel = this.getCurrentIfcModel();
    if (!ifcModel) {
      console.error('❌ IFCService: No hay modelo IFC cargado');
      return false;
    }
    try {
      // Actualizar la instancia del IFCService
      ifcModel.setElevation(elevationMeters, type);
      
      // También actualizar la instancia del GUI si está disponible
      try {
        // Intentar obtener el MapService y actualizar el ifcModel del GUI
        const { getMapService } = await import('../map/mapService');
        const mapService = getMapService();
        if (mapService) {
          const gui = mapService.getGUI();
          if (gui && gui.ifcModel) {
            const guiIfcModel = gui.ifcModel;
            if (guiIfcModel && typeof guiIfcModel.setElevation === 'function') {
              guiIfcModel.setElevation(elevationMeters, type);
              logger.log('IFC_SERVICE', `📍 Elevación sincronizada con GUI: ${elevationMeters.toFixed(2)} m (${type})`);
            }
          }
        }
      } catch (error) {
        logger.warn('IFC_SERVICE', '⚠️ No se pudo sincronizar elevación con GUI:', error);
      }
      
      return true;
    } catch (e) {
      console.error('❌ IFCService: Error al establecer elevación:', e);
      return false;
    }
  }

  /**
   * Obtiene la elevación actual del modelo IFC
   */
  public getModelElevation(): number {
    const ifcModel = this.getCurrentIfcModel();
    if (!ifcModel) {
      console.error('❌ IFCService: No hay modelo IFC cargado');
      return 0;
    }
    return ifcModel.getElevation();
  }

  /**
   * Obtiene el tipo de elevación actual del modelo IFC
   */
  public getModelElevationType(): 'ortometric' | 'ellipsoidal' {
    const ifcModel = this.getCurrentIfcModel();
    if (!ifcModel) {
      console.error('❌ IFCService: No hay modelo IFC cargado');
      return 'ortometric';
    }
    return ifcModel.getElevationType();
  }

  /**
   * Obtiene la geometría de un modelo específico
   */
  public getGeometryData(modelId: string): IFCGeometryData | null {
    return this.geometryCache.get(modelId) || null;
  }

  /**
   * Obtiene la geometría del modelo actual
   */
  public getCurrentGeometryData(): IFCGeometryData | null {
    logger.log('IFC_SERVICE', `🔍 getCurrentGeometryData: currentModelId=${this.currentModelId}, cacheSize=${this.geometryCache.size}`);
    
    // Si no hay currentModelId pero hay modelos en el cache, intentar recuperar el primero
    if (!this.currentModelId && this.geometryCache.size > 0) {
      const firstModelId = this.geometryCache.keys().next().value;
      if (firstModelId) {
        logger.log('IFC_SERVICE', `🔄 Recuperando modelo cargado en getCurrentGeometryData: ${firstModelId}`);
        this.currentModelId = firstModelId;
      }
    }
    
    if (!this.currentModelId) {
      logger.warn('IFC_SERVICE', '⚠️ No hay currentModelId');
      return null;
    }
    
    const data = this.geometryCache.get(this.currentModelId);
    logger.log('IFC_SERVICE', `🔍 getCurrentGeometryData: data=${!!data}`);
    return data || null;
  }

  /**
   * Obtiene la instancia de ifcModel del modelo actual
   */
  public getCurrentIfcModel(): ifcModel | null {
    const data = this.getCurrentGeometryData();
    logger.log('IFC_SERVICE', `🔍 getCurrentIfcModel: currentModelId=${this.currentModelId}, data=${!!data}, ifcModelInstance=${!!data?.ifcModelInstance}`);
    return data?.ifcModelInstance || null;
  }

  /**
   * Establece el modelo actual por ID
   */
  public setCurrentModel(modelId: string): boolean {
    if (this.geometryCache.has(modelId)) {
      this.currentModelId = modelId;
      logger.log('IFC_SERVICE', `✅ Modelo actual establecido: ${modelId}`);
      return true;
    } else {
      logger.warn('IFC_SERVICE', `⚠️ No se encontró modelo con ID: ${modelId}`);
      return false;
    }
  }

  /**
   * Obtiene el ID del modelo actual
   */
  public getCurrentModelId(): string | null {
    return this.currentModelId;
  }

  /**
   * Dibuja una planta específica del modelo actual
   */
  public drawStorey(storeyId: string): boolean {
    const ifcModel = this.getCurrentIfcModel();
    if (!ifcModel) {
      console.error('❌ IFCService: No hay modelo IFC cargado');
      return false;
    }

    try {
      ifcModel.updadteSelectedStoreyIds([storeyId]);
      
      // Obtener categorías disponibles para esta planta específica
      const availableCategoriesForStorey = ifcModel.getAvailableCategoriesForStorey(storeyId);
      logger.log('IFC_SERVICE', '🎨 IFCService: Categorías disponibles para la planta', storeyId, ':', availableCategoriesForStorey);
      
      // Seleccionar TODAS las categorías de esta planta por defecto si no hay ninguna seleccionada
      if (ifcModel.selectedCategoriesIds.length === 0) {
        ifcModel.selectedCategoriesIds = availableCategoriesForStorey;
        logger.log('IFC_SERVICE', '🎨 IFCService: Todas las categorías de la planta seleccionadas por defecto:', availableCategoriesForStorey);
      }
      
      ifcModel.drawFilteredPlan();
      logger.log('IFC_SERVICE', '✅ IFCService: Planta dibujada:', storeyId);
      return true;
    } catch (error) {
      console.error('❌ IFCService: Error al dibujar planta:', error);
      return false;
    }
  }

  /**
   * Filtra categorías del modelo actual
   */
  public filterCategories(categoryIds: string[]): boolean {
    const ifcModel = this.getCurrentIfcModel();
    if (!ifcModel) {
      console.error('❌ IFCService: No hay modelo IFC cargado');
      return false;
    }

    try {
      // Si no hay categorías seleccionadas, seleccionar todas
      if (categoryIds.length === 0) {
        const allCategories = ifcModel.getAvailableCategories();
        ifcModel.selectedCategoriesIds = allCategories;
        logger.log('IFC_SERVICE', '🎨 IFCService: Todas las categorías seleccionadas (filtro vacío):', allCategories);
      } else {
        ifcModel.selectedCategoriesIds = categoryIds;
        logger.log('IFC_SERVICE', '✅ IFCService: Categorías filtradas:', categoryIds);
      }
      
      ifcModel.drawFilteredPlan();
      return true;
    } catch (error) {
      console.error('❌ IFCService: Error al filtrar categorías:', error);
      return false;
    }
  }

  /**
   * Limpia la geometría de un modelo específico
   */
  public clearModel(modelId: string): boolean {
    const data = this.geometryCache.get(modelId);
    if (data && data.ifcModelInstance) {
      // Limpiar la capa BIM
      if (data.ifcModelInstance.bimLayer) {
        data.ifcModelInstance.bimLayer.getSource().clear();
      }
    }
    
    this.geometryCache.delete(modelId);
    
    if (this.currentModelId === modelId) {
      this.currentModelId = null;
    }
    
    logger.log('IFC_SERVICE', '✅ IFCService: Modelo limpiado:', modelId);
    return true;
  }

  /**
   * Limpia todos los modelos
   */
  public clearAllModels(): void {
    this.geometryCache.forEach((_, modelId) => {
      this.clearModel(modelId);
    });
    logger.log('IFC_SERVICE', '✅ IFCService: Todos los modelos limpiados');
  }

  /**
   * Obtiene información de todos los modelos en caché
   */
  public getCachedModels(): Array<{ modelId: string; name: string; isLoaded: boolean }> {
    return Array.from(this.geometryCache.entries()).map(([modelId, data]) => ({
      modelId,
      name: data.ifcModelInstance.getIfcmetadataName() || 'Modelo sin nombre',
      isLoaded: data.isLoaded
    }));
  }

  /**
   * Verifica si hay un modelo cargado
   */
  public hasLoadedModel(): boolean {
    // Si hay un currentModelId válido, verificar que existe en el cache
    if (this.currentModelId !== null && this.geometryCache.has(this.currentModelId)) {
      return true;
    }
    
    // Si no hay currentModelId pero hay modelos en el cache, intentar recuperar el primero
    if (this.currentModelId === null && this.geometryCache.size > 0) {
      const firstModelId = this.geometryCache.keys().next().value;
      if (firstModelId) {
        logger.log('IFC_SERVICE', `🔄 Recuperando modelo cargado: ${firstModelId}`);
        this.currentModelId = firstModelId;
        return true;
      }
    }
    
    return false;
  }

  /**
   * Obtiene las categorías disponibles para una planta específica
   */
  public getAvailableCategoriesForStorey(storeyId: string): string[] {
    const ifcModel = this.getCurrentIfcModel();
    if (!ifcModel) {
      console.error('❌ IFCService: No hay modelo IFC cargado');
      return [];
    }
    return ifcModel.getAvailableCategoriesForStorey(storeyId);
  }

  public getCategoryCountsForStorey(storeyId: string): Record<string, number> {
    const ifcModel = this.getCurrentIfcModel();
    if (!ifcModel) {
      console.error('❌ IFCService: No hay modelo IFC cargado');
      return {};
    }

    const tree = ifcModel.getGeoElementsTreeXZ();
    logger.log('IFC_SERVICE', '🔍 IFCService: geoElementsTreeXZ completo:', tree);
    logger.log('IFC_SERVICE', '🔍 IFCService: Claves disponibles en geoElementsTreeXZ:', Object.keys(tree));
    logger.log('IFC_SERVICE', '🔍 IFCService: Buscando storeyId:', storeyId);
    
    const counts: Record<string, number> = {};
    
    if (!tree[storeyId]) {
      logger.warn('IFC_SERVICE', `⚠️ IFCService: No se encontraron datos para la planta ${storeyId}`);
      logger.log('IFC_SERVICE', '🔍 IFCService: StoreyIds disponibles:', Object.keys(tree));
      return counts;
    }
    
    for (const category of Object.keys(tree[storeyId])) {
      const elements = tree[storeyId][category];
      counts[category] = Object.keys(elements).length;
    }
    
    logger.log('IFC_SERVICE', `📊 IFCService: Categorías encontradas para planta ${storeyId}:`, counts);
    return counts;
  }
}

export default IFCService;
